/**
 * Faixas de aderência da Curadoria Técnica.
 *
 * Espelham os cortes de `calcStars` na API (utils/curatorshipScore.js) e as
 * mesmas faixas aceitas pelo filtro `curatorship_range` do marketplace.
 * O limite superior é exclusivo (exceto o topo) para que um produto com
 * exatamente 30% caia em uma faixa só.
 *
 * As cores vêm dos tokens `primary-*` do design system (src/lib/tailwind-colors.ts),
 * que já são a mesma família oliva usada no mockup da régua.
 */
export type TCuratorshipTier = {
  /** Chave usada como valor do filtro `curatorship_range` na querystring. */
  key: string
  min: number
  max: number
  /** Largura do segmento na régua, em % do trilho. */
  width: number
  className: string
}

export const CURATORSHIP_TIERS: TCuratorshipTier[] = [
  { key: '0-20', min: 0, max: 20, width: 20, className: 'bg-primary-200' },
  { key: '20-30', min: 20, max: 30, width: 10, className: 'bg-primary-400' },
  { key: '30-50', min: 30, max: 50, width: 20, className: 'bg-primary-500' },
  { key: '50-70', min: 50, max: 70, width: 20, className: 'bg-primary-600' },
  { key: '70-90', min: 70, max: 90, width: 20, className: 'bg-primary-700' },
  { key: '90-100', min: 90, max: 100, width: 10, className: 'bg-primary-900' }
]

/** Marcas exibidas sob o trilho da régua. */
export const CURATORSHIP_TICKS = [0, 20, 30, 50, 70, 90, 100]

/**
 * Abaixo deste percentual a classificação não é exibida no produto.
 *
 * Preserva o comportamento anterior às estrelas: `calcStars` devolvia 0 abaixo
 * de 20% e o componente de estrelas escondia quando `stars <= 0`. Se o produto
 * decidir expor a faixa "Readequação" na vitrine, basta zerar esta constante.
 */
export const MIN_VISIBLE_PERCENTAGE = 20

export const clampPercentage = (value?: number | null): number | null => {
  if (value === null || value === undefined) return null

  const parsed = Number(value)
  if (Number.isNaN(parsed)) return null

  return Math.max(0, Math.min(100, parsed))
}

export const getCuratorshipTier = (percentage: number): TCuratorshipTier =>
  CURATORSHIP_TIERS.find((tier) => percentage >= tier.min && percentage < tier.max) ??
  CURATORSHIP_TIERS[CURATORSHIP_TIERS.length - 1]

export const formatPercentage = (percentage: number): string => `${Math.round(percentage)}%`
